import React, { useState, useMemo } from 'react';
import { useFileManagerStore, UploadTask } from '../../store/useFileManagerStore';
import { Minimize2, Maximize2, X, RefreshCw, CheckCircle, AlertCircle, Loader2, PauseCircle, Clock, UploadCloud, Trash2, Ban } from 'lucide-react';
import { createPortal } from 'react-dom';
import { t } from '../../i18n';

const formatBytes = (n: number): string => {
  if (!n || n < 0) n = 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};
const formatSpeed = (bps: number): string => (bps > 0 ? `${formatBytes(bps)}/s` : '');

const UploadManager: React.FC = () => {
  const { uploadTasks, cancelUploadTask, retryUploadTask, clearCompletedTasks, isUploadManagerOpen, toggleUploadManager, requestCancelBatch, retryBatch, cancelBatchConfirmation, resolveCancelBatch } = useFileManagerStore();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');

  const batches = useMemo(() => {
      const groups: Record<string, UploadTask[]> = {};
      uploadTasks.forEach(t => {
          if (!groups[t.batchId]) groups[t.batchId] = [];
          groups[t.batchId].push(t);
      });
      return Object.values(groups).sort((a, b) => b[0].timestamp - a[0].timestamp);
  }, [uploadTasks]);

  const currentBatches = batches.filter(batch => batch.some(t => t.status !== 'success'));
  const historyBatches = batches.filter(batch => batch.every(t => t.status === 'success'));

  if (!isUploadManagerOpen) return null;

  const renderBatch = (batch: UploadTask[], isHistory: boolean) => {
      const batchId = batch[0].batchId;
      const total = batch.length;
      const completed = batch.filter(t => t.status === 'success').length;
      const failed = batch.filter(t => t.status === 'error' || t.status === 'cancelled').length;
      const uploading = batch.filter(t => t.status === 'uploading').length;
      const pending = batch.filter(t => t.status === 'pending').length;
      
      const totalBytes = batch.reduce((a, t) => a + (t.size || 0), 0);
      const uploadedBytes = batch.reduce(
          (a, t) => a + (t.status === 'success' ? (t.size || 0) : (t.uploadedBytes || 0)), 0);
      const batchSpeed = batch.reduce(
          (a, t) => a + (t.status === 'uploading' ? (t.speed || 0) : 0), 0);
      const overallProgress = totalBytes > 0
          ? (uploadedBytes / totalBytes) * 100
          : (batch.reduce((acc, t) => acc + t.progress, 0) / total);

      // Optimization: limit rendered items for large batches
      let visibleTasks = batch;
      if (total > 50) {
          const important = batch.filter(t => ['error', 'uploading'].includes(t.status));
          const others = batch.filter(t => !['error', 'uploading'].includes(t.status));
          visibleTasks = [...important, ...others].slice(0, 50);
      }
      
      return (
          <div key={batchId} className="bg-white/5 p-3 rounded-lg mb-3">
              <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                      <Clock size={14} className="text-gray-400" />
                      <span className="text-xs text-gray-400">{new Date(batch[0].timestamp).toLocaleTimeString()}</span>
                      <span className="text-xs font-medium text-white">{t('uploadBatch', { id: batchId })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 mr-2 font-mono">{completed} / {total}</span>
                      {!isHistory && (
                          <>
                              {(failed > 0 || batch.some(t => t.status === 'cancelled')) && (
                                  <button onClick={() => retryBatch(batchId)} className="p-1 hover:bg-white/10 rounded" title={t('uploadRetryBatch')}>
                                      <RefreshCw size={14} className="text-yellow-400" />
                                  </button>
                              )}
                              <button onClick={() => requestCancelBatch(batchId)} className="p-1 hover:bg-white/10 rounded" title={t('uploadCancelBatch')}>
                                  <X size={14} className="text-red-400" />
                              </button>
                          </>
                      )}
                  </div>
              </div>
              <div className="w-full bg-white/10 h-1 rounded-full mb-1 overflow-hidden">
                  <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${overallProgress}%` }} />
              </div>
              <div className="flex items-center justify-between text-[11px] text-gray-400 mb-2 font-mono">
                  <span>{formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}</span>
                  <span className="text-blue-300">{batchSpeed > 0 ? formatSpeed(batchSpeed) : ''}</span>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                  {visibleTasks.map(task => (
                      <div key={task.id} className="group">
                          <div className="flex items-center justify-between text-sm gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                  {task.status === 'uploading' && <Loader2 size={14} className="animate-spin text-blue-400 shrink-0" />}
                                  {task.status === 'success' && <CheckCircle size={14} className="text-green-400 shrink-0" />}
                                  {task.status === 'error' && (
                                      <span title={task.error || '上传失败'} className="shrink-0 cursor-help flex">
                                          <AlertCircle size={14} className="text-red-400" />
                                      </span>
                                  )}
                                  {task.status === 'pending' && <Clock size={14} className="text-gray-500 shrink-0" />}
                                  {task.status === 'cancelled' && <PauseCircle size={14} className="text-yellow-400 shrink-0" />}
                                  <span className="truncate text-gray-300" title={task.name}>{task.name}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                  {task.status === 'error' && (
                                      <button onClick={() => retryUploadTask(task.id)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded">
                                          <RefreshCw size={12} className="text-yellow-400" />
                                      </button>
                                  )}
                                  {['pending', 'uploading'].includes(task.status) && (
                                      <button onClick={() => cancelUploadTask(task.id)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded">
                                          <X size={12} className="text-red-400" />
                                      </button>
                                  )}
                                  <div className="flex flex-col items-end shrink-0 leading-tight whitespace-nowrap">
                                      <span className="text-[11px] text-gray-400 font-mono whitespace-nowrap">
                                          {formatBytes(task.uploadedBytes ?? (task.status === 'success' ? task.size : 0))} / {formatBytes(task.size)}
                                      </span>
                                      <span className="text-[10px] text-gray-500">
                                          {Math.round(task.progress)}%{task.status === 'uploading' && task.speed ? ` · ${formatSpeed(task.speed)}` : ''}
                                      </span>
                                  </div>
                              </div>
                          </div>
                          {task.status === 'error' && (
                              <div className="ml-6 mt-0.5 text-[11px] leading-snug text-red-400/90 break-all" title={task.error || '上传失败'}>
                                  {task.error || '上传失败（无错误详情）'}
                              </div>
                          )}
                      </div>
                  ))}
                  {total > visibleTasks.length && (
                      <div className="text-center text-xs text-gray-500 py-1 border-t border-white/5 mt-1">
                          {t('uploadMoreItems', { count: total - visibleTasks.length })}
                      </div>
                  )}
              </div>
          </div>
      );
  };

  return (
    <>
        <div data-ui-element data-upload-manager className={`fixed bottom-4 right-4 z-[1000] w-96 bg-[#202020] border border-white/10 rounded-lg shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${isCollapsed ? 'h-10' : 'h-[500px]'}`}>
            {/* Header */}
            <div className="h-10 bg-[#252525] border-b border-white/5 flex items-center justify-between px-3 shrink-0">
                <div className="flex items-center gap-2">
                    <UploadCloud size={16} className="text-blue-400" />
                    <span className="font-medium text-sm text-gray-200">{t('uploadTitle')} ({uploadTasks.filter(t => t.status === 'uploading').length})</span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => setIsCollapsed(!isCollapsed)} className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors">
                        {isCollapsed ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
                    </button>
                    <button onClick={toggleUploadManager} className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors">
                        <X size={14} />
                    </button>
                </div>
            </div>
            
            {!isCollapsed && (
                <>
                    <div className="flex border-b border-white/5">
                        <button 
                            onClick={() => setActiveTab('current')}
                            className={`flex-1 py-2 text-xs font-medium transition-colors ${activeTab === 'current' ? 'text-blue-400 border-b-2 border-blue-400 bg-white/5' : 'text-gray-400 hover:bg-white/5'}`}
                        >
                            {t('uploadCurrent')} ({currentBatches.length})
                        </button>
                        <button 
                            onClick={() => setActiveTab('history')}
                            className={`flex-1 py-2 text-xs font-medium transition-colors ${activeTab === 'history' ? 'text-blue-400 border-b-2 border-blue-400 bg-white/5' : 'text-gray-400 hover:bg-white/5'}`}
                        >
                            {t('uploadHistory')} ({historyBatches.length})
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 custom-scrollbar">
                        {activeTab === 'current' ? (
                            currentBatches.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-gray-500 space-y-2">
                                    <CheckCircle size={24} className="opacity-50" />
                                    <span className="text-sm">{t('uploadAllComplete')}</span>
                                </div>
                            ) : (
                                currentBatches.map(b => renderBatch(b, false))
                            )
                        ) : (
                            historyBatches.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-gray-500 space-y-2">
                                    <Clock size={24} className="opacity-50" />
                                    <span className="text-sm">{t('uploadNoHistory')}</span>
                                </div>
                            ) : (
                                <>
                                    <div className="flex justify-end mb-2">
                                        <button onClick={clearCompletedTasks} className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
                                            {t('uploadClearHistory')}
                                        </button>
                                    </div>
                                    {historyBatches.map(b => renderBatch(b, true))}
                                </>
                            )
                        )}
                    </div>
                </>
            )}
        </div>

        {/* Confirmation Modal */}
        {cancelBatchConfirmation && createPortal(
            <div data-ui-element className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[2000] flex items-center justify-center">
                <div className="bg-[#252525] border border-white/10 rounded-lg shadow-2xl p-6 max-w-sm w-full">
                    <h3 className="text-lg font-medium text-white mb-2">{t('uploadCancelTitle')}</h3>
                    <p className="text-gray-400 text-sm mb-6">
                        {t('uploadCancelDesc')}
                    </p>
                    <div className="flex flex-col gap-2">
                        <button 
                            onClick={() => resolveCancelBatch('yes')}
                            className="flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 py-2 rounded transition-colors"
                        >
                            <Trash2 size={16} />
                            {t('uploadCancelYesDelete')}
                        </button>
                        <button 
                            onClick={() => resolveCancelBatch('no')}
                            className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white py-2 rounded transition-colors"
                        >
                            <Ban size={16} />
                            {t('uploadCancelNoKeep')}
                        </button>
                        <button 
                            onClick={() => resolveCancelBatch('cancel')}
                            className="text-gray-500 hover:text-gray-400 text-sm py-2 mt-2"
                        >
                            {t('uploadCancelContinue')}
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        )}
    </>
  );
};

export default UploadManager;
